import React, { useState, useCallback, useEffect, useMemo, memo } from 'react';
import { useDispatch } from 'react-redux';
import { GlobalHotKeys } from 'react-hotkeys';
import { v4 as uuidv4 } from 'uuid';
import { useFlags } from 'launchdarkly-react-client-sdk';

import QueryParamConstants from 'Shared/constants/QueryParams.constants';
import { useQueryParam } from 'Shared/utils/hooks';
import { useWorkspaceAgendaAi } from 'Shared/utils/hooks/use-workspace-agenda-ai';

import { jsonEmptyContentState, jsonContentStateFromString } from 'Shared/utils/draftJsHelpers';
import { updateSortedDiscussionTopicsUuids } from 'Shared/actions/sortedDiscussionTopicsUuids';
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';
import { addItem, nestTodo } from 'Shared/actions/todos';
import { ItemDragLayer } from 'Shared/components/DragAndDropComponents';
import { getItemsForStandardView } from 'Shared/selectors/workspace';
import { useDeepEqualSelector, useDeepCompareMemoize } from 'Shared/utils/hooks';
import { ItemType } from 'Shared/constants/types';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButton from 'Wpds/components/button/WpButton';

import DiscussionTopicsCreatePanel from './DiscussionTopicsCreatePanel/DiscussionTopicsCreatePanel';
import WorkspaceItemsList from '../WorkspaceItemsList/WorkspaceItemsList';
import VideoRecorder from 'Shared/components/VideoRecorder/VideoRecorder';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';
import AgendaAiSuggestedTopics from './AgendaAiSuggestedTopics/AgendaAiSuggestedTopics';
import PiSuggestedTopics from 'Dashboard/components/PiSuggestedTopics/PiSuggestedTopics';

import './DiscussionTopics.scss';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useApp } from 'Shared/utils/hooks/use-app';

const keyMap = {
  ADD_ITEM: ['ctrl+shift+i', 'command+shift+i'],
  ADD_SECTION: ['ctrl+shift+s', 'command+shift+s'],
};

type Props = {
  discussionTopicsTodosContentRef: {
    current: HTMLDivElement;
  };
  onFocusedTodoUpdate: Function;
  showToolbar?: boolean;
  isCompleteToggleDisabled?: boolean;
  customActionsArea?: Function;
};

function DiscussionTopics({
  discussionTopicsTodosContentRef,
  onFocusedTodoUpdate,
  showToolbar = true,
  customActionsArea,
  isCompleteToggleDisabled,
}: Props) {
  const dispatch = useDispatch();
  const { id: workspaceId, isPersonal } = useWorkspace();
  const { isZoom } = useApp();

  const { featureFlagEnablePiSuggested } = useFlags();
  const { isSlackAiActive } = useWorkspaceAgendaAi({ fetchAvailableChannels: false });

  const [, setShowModal] = useQueryParam(QueryParamConstants.TEMPLATES_MODAL);

  const discussionTopics = useDeepEqualSelector(state =>
    getItemsForStandardView(state.workspace.discussion_topics)
      .filter(({ workspaceId: meetigItemWorkspaceId }) => meetigItemWorkspaceId === workspaceId)
      .map(({ uuid, parentUuid, isSection }) => ({ uuid, parentUuid, isSection }))
  );
  const sortedDiscussionTopicsUuids = useDeepEqualSelector(state => state.sortedDiscussionTopicsUuids);
  const [isShowVideoRecorder, setIsShowVideoRecorder] = useState(false);

  useEffect(() => {
    dispatch(updateSortedDiscussionTopicsUuids(discussionTopics.map(({ uuid }) => uuid)));
  }, [useDeepCompareMemoize(discussionTopics)]);

  const handleSelectedItemUpdate = useCallback(
    ({ itemUuid, isShiftPressed = false }) =>
      setTimeout(() => {
        onFocusedTodoUpdate(itemUuid);
        dispatch(updateSelectedUuids(itemUuid, { isOneSelected: true, isShiftPressed }));
      }, 0),
    [dispatch, onFocusedTodoUpdate]
  );

  const handleAddMeetingItem = useCallback(
    ({
      jsonBody,
      isSuggestedTopic = false,
      asanaData = null,
      servicenowData = null,
      assignmentInfo,
      isAssignCurrentUser,
      isSection = false,
    } = {}) => {
      const body = jsonBody || jsonEmptyContentState();
      const itemUuid = uuidv4();
      dispatch(
        addItem({
          itemUuid,
          isSuggestedTopic,
          isSection,
          body,
          workspaceId,
          position: 1,
          itemType: ItemType.discussionTopic,
          asanaData,
          servicenowData,
          assignmentInfo,
          isAssignCurrentUser,
        })
      );

      // Focus on the first element, if it exists.
      handleSelectedItemUpdate({ itemUuid });
    },
    [dispatch, workspaceId, handleSelectedItemUpdate]
  );

  const handlers = useMemo(
    () => ({
      ADD_ITEM: event => {
        event.preventDefault();
        handleAddMeetingItem();
      },
    }),
    [handleAddMeetingItem]
  );

  const handleVideoRecorderToggle = useCallback(
    () => setIsShowVideoRecorder(prevIsShowVideoRecorder => !prevIsShowVideoRecorder),
    []
  );

  const handleArrowUpClick = useCallback(
    ({ uuid: currentItemUuid, isShiftPressed = false }) => {
      const curentItemIndex = sortedDiscussionTopicsUuids.indexOf(
        sortedDiscussionTopicsUuids.find(itemUuid => itemUuid === currentItemUuid)
      );
      const prevItemUuid = sortedDiscussionTopicsUuids[curentItemIndex - 1];
      if (prevItemUuid) {
        handleSelectedItemUpdate({ itemUuid: prevItemUuid, isShiftPressed });
      }
    },
    [sortedDiscussionTopicsUuids, handleSelectedItemUpdate]
  );

  const handleArrowDownClick = useCallback(
    ({ uuid: currentItemUuid, isShiftPressed = false }) => {
      const curentItemIndex = sortedDiscussionTopicsUuids.indexOf(
        sortedDiscussionTopicsUuids.find(itemUuid => itemUuid === currentItemUuid)
      );
      const nextItemUuid = sortedDiscussionTopicsUuids[curentItemIndex + 1];
      if (nextItemUuid) {
        handleSelectedItemUpdate({ itemUuid: nextItemUuid, isShiftPressed });
      }
    },
    [sortedDiscussionTopicsUuids, handleSelectedItemUpdate]
  );

  const handleNestingByDrag = useCallback(
    ({ draggedItemUuids, hoveredItemUuid, hoveredItemId }) => {
      dispatch(
        nestTodo({
          draggedItemUuids,
          parentId: hoveredItemId,
          parentUuid: hoveredItemUuid,
          itemType: ItemType.discussionTopic,
          workspaceId,
          byDrag: true,
        })
      );
    },
    [dispatch, workspaceId]
  );

  const handleVideoItemAdd = useCallback(
    video => {
      const body = jsonContentStateFromString(video.audioOnly ? 'New Audio Item' : 'New Video Item');
      const itemUuid = uuidv4();
      dispatch(addItem({ itemUuid, body, position: 1, workspaceId, video, itemType: ItemType.discussionTopic }));
    },
    [dispatch, workspaceId]
  );

  const showEmptyState = discussionTopics.length === 0;

  return (
    <GlobalHotKeys keyMap={keyMap} handlers={handlers}>
      <ItemDragLayer />
      <div className="action-items workspace-discussion-topics" ref={discussionTopicsTodosContentRef}>
        {showToolbar && (
          <DiscussionTopicsCreatePanel
            onAddItem={handleAddMeetingItem}
            onSelectedItemUpdate={handleSelectedItemUpdate}
            isZoom={isZoom}
            isEmpty={showEmptyState}
          />
        )}
        {isSlackAiActive && !featureFlagEnablePiSuggested && <AgendaAiSuggestedTopics />}
        {featureFlagEnablePiSuggested && (
          <div className="workspace-discussion-topics__pi-suggested-topics">
            <PiSuggestedTopics />
          </div>
        )}
        {isZoom && showEmptyState && (
          <div className="workspace-discussion-topics-zooom-empty">
            <WpIcon name="edit" />
            <span className="workspace-discussion-topics-zooom-empty-label">Your meeting agenda is empty. </span>
            <div>
              <WpButton onClick={handleAddMeetingItem}>
                <WpIcon name="plus-circle" />
                Add topic
              </WpButton>
            </div>
            <span className="workspace-discussion-topics-zooom-empty-label">or</span>
            <div>
              <WpButton variant="tertiary" onClick={() => setShowModal('workspace')}>
                <WpIcon name="copy" />
                Add template
              </WpButton>
            </div>
          </div>
        )}
        {!isZoom && showEmptyState ? (
          <EmptyStateContainer
            subTitle={
              isPersonal
                ? "Use this area to save discussion topics or notes that you aren't ready to share."
                : 'Start the collaborative agenda by adding a Discussion Topic.'
            }
            button={
              <WpButton variant="tertiary" onClick={handleAddMeetingItem}>
                <WpIcon name="plus" />
                Add Topic
              </WpButton>
            }
            inContainer
          />
        ) : (
          <WorkspaceItemsList
            listOfParentUuid={null}
            items={discussionTopics}
            listType={ItemType.discussionTopic}
            onNestByDrag={handleNestingByDrag}
            onArrowUpClick={handleArrowUpClick}
            onArrowDownClick={handleArrowDownClick}
            customActionsArea={customActionsArea}
            isCompleteToggleDisabled={isCompleteToggleDisabled}
          />
        )}
        {isShowVideoRecorder && <VideoRecorder onSave={handleVideoItemAdd} onHide={handleVideoRecorderToggle} />}
      </div>
    </GlobalHotKeys>
  );
}

export default memo(DiscussionTopics);
